import React, { useState } from 'react';
import Sidebar from '../../components/shared/Sidebar';
import Header from '../../components/shared/Header';
import AlertBanner from '../../components/shared/AlertBanner';
import TransactionMetricCard from '../../components/transactions/TransactionMetricCard';
import TransactionFilters from '../../components/transactions/TransactionFilters';
import TransactionTable from '../../components/transactions/TransactionTable';
import './Transactions.css';

interface Transaction {
  id: string;
  orderId: string;
  customer: string;
  amount: string;
  fee: string;
  net: string;
  method: string;
  status: 'completed' | 'pending' | 'failed' | 'refunded';
  dateTime: string;
}

const Transactions: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [methodFilter, setMethodFilter] = useState('All Methods');
  const [searchQuery, setSearchQuery] = useState('');

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Sample transaction data
  const transactions: Transaction[] = [
    { id: 'TXN-98765', orderId: 'ORD-1234', customer: 'Sarah Johnson', amount: '$45.99', fee: '$2.30', net: '$43.69', method: 'Credit Card', status: 'completed', dateTime: '2025-11-07 14:32' },
    { id: 'TXN-98764', orderId: 'ORD-1233', customer: 'Mike Chen', amount: '$32.50', fee: '$1.63', net: '$30.87', method: 'Debit Card', status: 'completed', dateTime: '2025-11-07 14:20' },
    { id: 'TXN-98763', orderId: 'ORD-1232', customer: 'Emma Davis', amount: '$67.20', fee: '$3.36', net: '$63.84', method: 'Credit Card', status: 'pending', dateTime: '2025-11-07 14:15' },
    { id: 'TXN-98762', orderId: 'ORD-1231', customer: 'Tom Wilson', amount: '$28.75', fee: '$1.44', net: '$27.31', method: 'Digital Wallet', status: 'completed', dateTime: '2025-11-07 14:08' },
    { id: 'TXN-98761', orderId: 'ORD-1230', customer: 'Lisa Brown', amount: '$54.30', fee: '$2.72', net: '$51.58', method: 'Credit Card', status: 'failed', dateTime: '2025-11-07 14:00' },
    { id: 'TXN-98760', orderId: 'ORD-1229', customer: 'James Lee', amount: '$89.40', fee: '$4.47', net: '$84.93', method: 'Credit Card', status: 'completed', dateTime: '2025-11-07 13:45' },
    { id: 'TXN-98759', orderId: 'ORD-1228', customer: 'Anna Taylor', amount: '$43.20', fee: '$2.16', net: '$41.04', method: 'Debit Card', status: 'refunded', dateTime: '2025-11-07 13:30' },
    { id: 'TXN-98758', orderId: 'ORD-1227', customer: 'Chris Martin', amount: '$25.80', fee: '$1.29', net: '$24.51', method: 'Digital Wallet', status: 'completed', dateTime: '2025-11-07 13:22' },
  ];

  const totalTransactions = 1234;
  const transactionsPerPage = 8;
  const totalPages = Math.ceil(totalTransactions / transactionsPerPage);

  // Filter transactions
  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = 
      transaction.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      transaction.orderId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      transaction.customer.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'All Status' || 
      transaction.status === statusFilter.toLowerCase();

    const matchesMethod = methodFilter === 'All Methods' || 
      transaction.method.toLowerCase() === methodFilter.toLowerCase();

    return matchesSearch && matchesStatus && matchesMethod;
  });

  // Calculate metrics
  const totalProcessed = '$45,678';
  const platformFees = '$2,284';
  const netRevenue = '$43,394';
  const failedCount = filteredTransactions.filter(t => t.status === 'failed').length;

  const handleSelectTransaction = (transactionId: string) => {
    setSelectedTransactions(prev =>
      prev.includes(transactionId)
        ? prev.filter(id => id !== transactionId)
        : [...prev, transactionId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTransactions.length === filteredTransactions.length) {
      setSelectedTransactions([]);
    } else {
      setSelectedTransactions(filteredTransactions.map(t => t.id));
    }
  };

  const handleReviewFailed = () => {
    // Navigate to failed transactions or open review modal
    console.log('Review failed transactions');
    setStatusFilter('Failed');
  };

  const handleExportTransactions = () => {
    // Export transactions to CSV
    const transactionsToExport = filteredTransactions;
    
    if (transactionsToExport.length === 0) {
      alert('No transactions to export');
      return;
    }

    // Create CSV headers
    const headers = ['Transaction ID', 'Order ID', 'Customer', 'Amount', 'Fee', 'Net', 'Method', 'Status', 'Date & Time'];
    
    // Create CSV rows
    const rows = transactionsToExport.map(transaction => [
      transaction.id,
      transaction.orderId,
      transaction.customer,
      transaction.amount,
      transaction.fee,
      transaction.net,
      transaction.method,
      transaction.status,
      transaction.dateTime
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `transactions_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Paginate filtered transactions
  const startIndex = (currentPage - 1) * transactionsPerPage;
  const endIndex = startIndex + transactionsPerPage;
  const paginatedTransactions = filteredTransactions.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, methodFilter]);

  return (
    <div className="dashboard-container">
      <Sidebar 
        collapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
      />
      <div className={`dashboard-main ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Header page="transactions" />
        <main className="dashboard-content">
          <div className="transactions-header-section">
            <div>
              <h1 className="transactions-title">Transactions</h1>
              <p className="transactions-subtitle">Payment and transaction history</p>
            </div>
            <button className="transactions-export-btn" onClick={handleExportTransactions}>
              <span>📥</span> Export Transactions
            </button>
          </div>

          {failedCount > 0 && (
            <AlertBanner
              message={`${failedCount} failed transaction${failedCount !== 1 ? 's' : ''} require attention`}
              description="Review and retry failed payments or contact customers"
              actionLabel="Review Failed"
              onAction={handleReviewFailed}
              variant="error"
            />
          )}

          <div className="transactions-metrics">
            <TransactionMetricCard
              title="Total Processed"
              value={totalProcessed}
              color="green"
              icon="✓"
            />
            <TransactionMetricCard
              title="Platform Fees"
              value={platformFees}
              color="default"
            />
            <TransactionMetricCard
              title="Net Revenue"
              value={netRevenue}
              color="green"
            />
            <TransactionMetricCard
              title="Failed Transactions"
              value={failedCount.toString()}
              color="red"
              icon="!"
            />
          </div>

          <TransactionFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            methodFilter={methodFilter}
            onMethodFilterChange={setMethodFilter}
          />

          <TransactionTable
            transactions={paginatedTransactions}
            selectedTransactions={selectedTransactions}
            onSelectTransaction={handleSelectTransaction}
            onSelectAll={handleSelectAll}
          />

          {totalTransactions > 0 && (
            <div className="transactions-pagination">
              <div className="pagination-info">
                Showing {((currentPage - 1) * transactionsPerPage) + 1} to {Math.min(currentPage * transactionsPerPage, totalTransactions)} of {totalTransactions} transactions
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => prev - 1)}
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  className="pagination-btn"
                  disabled={currentPage === totalPages || totalPages === 0}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Transactions;

